import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { ModalOverlay } from '@/components/ModalOverlay';
import { Decimal } from '@/lib/offline/decimal';
import { extractApiMessage } from '@/lib/api';

type Sexo = 'machos' | 'hembras';
type Fila = { edad: string; valor: string };
type Errores = { edad?: string; valor?: string };

function validate(kind: 'peso' | 'pienso', rows: Fila[]) {
  const errors: Errores[] = rows.map(() => ({}));
  const points = rows.map((row, index) => {
    const min = kind === 'peso' ? 0 : 1;
    const age = Number(row.edad);
    if (
      !/^\d+$/.test(row.edad.trim()) ||
      !Number.isSafeInteger(age) ||
      age < min ||
      age > 2147483647
    ) {
      errors[index].edad =
        kind === 'peso'
          ? 'Introduce un día entero entre 0 y 2.147.483.647.'
          : 'Introduce un día entero entre 1 y 2.147.483.647.';
    }
    let value: Decimal | null = null;
    try {
      const normalized = row.valor
        .trim()
        .replace(',', '.')
        .replace(/^([+-]?)\./, (_, sign: string) => `${sign}0.`);
      if (!normalized || !Number.isFinite(Number(normalized))) throw new Error();
      value = new Decimal(normalized);
      if (
        kind === 'peso'
          ? value.compare('0.001') < 0 || value.compare('999.999') > 0
          : value.compare(0) < 0 || value.compare('99999999.99') > 0
      )
        throw new Error();
    } catch {
      value = null;
      errors[index].valor =
        kind === 'peso'
          ? 'Introduce un peso entre 0,001 y 999,999 kg.'
          : 'Introduce kg acumulados entre 0 y 99.999.999,99.';
    }
    return { edad: age, valor: value, index };
  });
  const ages = new Map<number, number>();
  for (const point of points) {
    if (errors[point.index].edad) continue;
    const previous = ages.get(point.edad);
    if (previous != null) {
      errors[previous].edad = errors[point.index].edad =
        'Día repetido. Usa una edad distinta por punto.';
    } else ages.set(point.edad, point.index);
  }
  const sorted = [...points].sort((a, b) => a.edad - b.edad);
  if (kind === 'pienso') {
    for (let i = 1; i < sorted.length; i++) {
      const previous = sorted[i - 1],
        current = sorted[i];
      if (
        !errors[current.index].edad &&
        !errors[previous.index].edad &&
        current.valor != null &&
        previous.valor != null &&
        current.valor.compare(previous.valor) < 0
      ) {
        errors[current.index].valor = 'Los kg acumulados no pueden disminuir con la edad.';
      }
    }
  }
  return {
    errors,
    points: errors.some((error) => error.edad || error.valor)
      ? []
      : sorted.map((point) => ({ edad: point.edad, valor: point.valor!.toNumber() })),
  };
}

/** Shared editing rules for the two existing curve pages. HTTP writes stay in each page. */
export function useCurveEditor(kind: 'peso' | 'pienso') {
  const [sexo, setSexo] = useState<Sexo>('machos');
  const [filas, setFilas] = useState<Fila[]>([]);
  const [errores, setErrores] = useState<Errores[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [pendingSexo, setPendingSexo] = useState<Sexo | null>(null);
  const baseline = useRef('[]'),
    dirty = useRef(false),
    saving = useRef(false);
  const errorRef = useRef<HTMLDivElement>(null);
  dirty.current = JSON.stringify(filas) !== baseline.current;
  const blocker = useBlocker(() => dirty.current || saving.current);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useBeforeUnload((event) => {
    if (dirty.current || saving.current) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const hydrate = useCallback((rows: Fila[]) => {
    if (dirty.current || saving.current) return;
    baseline.current = JSON.stringify(rows);
    setFilas(rows);
  }, []);
  function changeRows(rows: Fila[]) {
    if (saving.current) return;
    setError(null);
    setErrores([]);
    setFilas(rows);
  }
  function applySexo(next: Sexo) {
    dirty.current = false;
    baseline.current = '[]';
    setFilas([]);
    setErrores([]);
    setError(null);
    setPendingSexo(null);
    setSexo(next);
  }
  function changeSexo(next: Sexo) {
    if (saving.current || next === sexo) return;
    if (dirty.current) setPendingSexo(next);
    else applySexo(next);
  }
  const validation = useMemo(() => validate(kind, filas), [kind, filas]);
  function startSave() {
    if (saving.current) return null;
    setErrores(validation.errors);
    if (!validation.points.length) {
      setError(
        filas.length
          ? 'Revisa los campos señalados antes de guardar.'
          : 'Añade al menos un punto antes de guardar.',
      );
      return null;
    }
    saving.current = true;
    setGuardando(true);
    setError(null);
    return validation.points;
  }
  function saved(confirmed: Fila[]) {
    baseline.current = JSON.stringify(confirmed);
    dirty.current = false;
    setFilas(confirmed);
    if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
    setPendingSexo(null);
  }
  function finishSave() {
    saving.current = false;
    setGuardando(false);
  }
  const id = (index: number, field: keyof Fila) => `curva-${kind}-${field}-${index}`;
  function fieldProps(index: number, field: keyof Fila) {
    return {
      id: id(index, field),
      disabled: guardando,
      'aria-invalid': !!errores[index]?.[field],
      'aria-describedby': errores[index]?.[field] ? `${id(index, field)}-error` : undefined,
    };
  }
  const errorSummary = error && (
    <div role="alert" tabIndex={-1} ref={errorRef} className="parte-error my-4">
      <p>{error}</p>
      {errores.flatMap((row, index) =>
        (['edad', 'valor'] as const).map(
          (field) =>
            row[field] && (
              <a
                key={id(index, field)}
                href={`#${id(index, field)}`}
                className="mt-1 block underline"
                onClick={(event) => {
                  event.preventDefault();
                  document.getElementById(id(index, field))?.focus();
                }}
              >
                Fila {index + 1}: {row[field]}
              </a>
            ),
        ),
      )}
    </div>
  );
  const routeBlocked = blocker.state === 'blocked';
  const dialog = (routeBlocked || pendingSexo) && (
    <ModalOverlay
      ariaLabel="Cambios sin guardar"
      placement="bottom"
      className="max-w-md"
      onBackdrop={() => {
        if (routeBlocked) blocker.reset();
        setPendingSexo(null);
      }}
    >
      <h2 className="ui-page-title">{guardando ? 'Guardando…' : 'Cambios sin guardar'}</h2>
      <p className="py-3">
        {guardando
          ? 'Espera a que termine el guardado antes de salir.'
          : 'Puedes seguir editando o salir sin guardar este borrador.'}
      </p>
      <button
        className="ui-primary w-full"
        onClick={() => {
          if (routeBlocked) blocker.reset();
          setPendingSexo(null);
        }}
      >
        Seguir editando
      </button>
      {!guardando && (
        <button
          className="ui-secondary mt-2 w-full"
          onClick={() => {
            dirty.current = false;
            if (routeBlocked) blocker.proceed();
            else if (pendingSexo) applySexo(pendingSexo);
          }}
        >
          Salir sin guardar
        </button>
      )}
    </ModalOverlay>
  );
  return {
    sexo,
    filas,
    errores,
    guardando,
    hydrate,
    changeRows,
    changeSexo,
    startSave,
    saved,
    finishSave,
    failSave: (err: unknown) => setError(extractApiMessage(err)),
    points: validation.points,
    fieldProps,
    errorSummary,
    dialog,
  };
}
